import { useState, useEffect, useRef } from 'react';
import { useAPI } from 'lib/API';
import { useAuth } from 'lib/Auth';
import { getReports, getReportsData } from 'requests/powerBiReports';
import { loadBrands } from 'requests/brand';
import { Role, SHRole, isBrandLevelRoles, isVendor, isVendorAdmin } from 'data/User';
import { useShowReportsPermission } from 'hooks/useUserRoles';
import { PermissionCode } from 'data/UserPermissions';
import Vendor from 'data/Vendor';
import Brand from 'data/Brand';
import FeatureFlag from 'data/FeatureFlag';
import { Report } from 'powerbi-client';
import { BusinessUnit } from 'data/BusinessUnit';
import { getItems } from 'lib/SessionStorage';
import { STORAGE_KEYS } from 'constants/general';

// Constants
const STOCKS_REPORT = 'Stocks Information';

// Types
export interface ReportData {
  id: number;
  viewCode: string;
  reportId: string;
  userRole: number;
}

export interface PowerBiTab {
  value: string;
  badge: {
    content: string;
    color: string;
    backgroundColor: string;
  };
  disabled?: boolean;
}

export interface ReportEmbedInfo {
  ReportId?: string;
  EmbedUrl?: string;
  EmbedToken?: string;
  Vendor?: string;
}

interface UsePowerBiReportsProps {
  selectedVendor?: Vendor;
  selectedBusinessUnit?: BusinessUnit;
  featureFlags: FeatureFlag;
  vendorsList: Vendor[];
}

export const usePowerBiReports = ({
  selectedVendor,
  selectedBusinessUnit,
  featureFlags,
  vendorsList,
}: UsePowerBiReportsProps) => {
  const { token, user } = useAuth();
  const [reportInfo, setReportInfo] = useState<ReportEmbedInfo>({});
  const [report, setReport] = useState<Report>();
  const [activeTab, setActiveTab] = useState<number | undefined>(0);
  const [reports, setReports] = useState<ReportData[]>([]);
  const [powerBiTabs, setPowerBiTabs] = useState<PowerBiTab[]>([]);
  const [stockTab, setStockTab] = useState<PowerBiTab[]>([]);
  const prevTabCountRef = useRef(0);

  // Permission checks
  const { show: showOrderInfo } = useShowReportsPermission(PermissionCode.ORDER_INFORMATION);
  const { show: showVendorOverview } = useShowReportsPermission(PermissionCode.VENDOR_OVERVIEW);
  const { show: showPurchasePlan } = useShowReportsPermission(PermissionCode.PURCHASE_PLAN);
  const { show: showRatingsReviews } = useShowReportsPermission(PermissionCode.RATINGS_REVIEWS);
  const { show: showStockInfo } = useShowReportsPermission(PermissionCode.STOCK_INFORMATION);
  const { show: showTransactionJournal } = useShowReportsPermission(PermissionCode.TRANSACTION_JOURNAL);
  const { show: showVendorInsights } = useShowReportsPermission(PermissionCode.VENDOR_INSIGHTS);
  const { show: showRevenueRecognition } = useShowReportsPermission(PermissionCode.REVENUE_RECOGNITION);
  const { show: showVendorsOfmpWebReport } = useShowReportsPermission(PermissionCode.VENDOR_OFMP_WEB_REPORT);
  const { show: showOfmpWebReport } = useShowReportsPermission(PermissionCode.OFMP_WEB_REPORT);

  // Mapping between tab names and permission flags
  const tabPermissionMap: { [key: string]: boolean } = {
    'order information': showOrderInfo,
    'vendor overview': showVendorOverview,
    'purchase plan': showPurchasePlan,
    'rating & reviews': showRatingsReviews,
    'stocks information': showStockInfo,
    'transaction journal': showTransactionJournal,
    'vendor insights': showVendorInsights,
    'revenue recognition compliance': showRevenueRecognition,
    'vendors ofmp web report': showVendorsOfmpWebReport,
    'ofmp web report': showOfmpWebReport,
  };

  /**
   * Helper function to get tab name by index
   */
  const getTabNameByIndex = (tabIndex: number): string => {
    return powerBiTabs[tabIndex]?.value || '';
  };

  /**
   * Convert role name to numeric ID
   */
  const getRoleId = (roleName?: string): number => {
    if (!roleName) return -1;

    const roleMap: Record<string, number> = {
      [Role.Admin]: 1,
      [Role.Vendor]: 2,
      [Role.SuperAdmin]: 3,
      [Role.VendorAdmin]: 4,
      [Role.BrandAdmin]: 4,
      [Role.Marketing]: 4,
      [Role.Finance]: 4,
      [Role.Sales]: 4,
      [Role.Fulfilment]: 4,
    };

    return roleMap[roleName] ?? -1;
  };

  /**
   * API calls
   */
  const getPowerBiReportReq = useAPI<ReportEmbedInfo>({
    deferFn: getReportsData,
    onResolve: (result) => setReportInfo(result),
    onReject: (err) => {
      setReportInfo({});
      console.error('Failed to fetch report data:', err);
    },
  });

  const brandsReq = useAPI<Brand[]>({
    deferFn: loadBrands,
    onResolve: () => { }
  });

  const getPowerBiReports = useAPI({
    deferFn: getReports,
    onResolve: (result) => setReports(result),
    onReject: (err) => {
      setReportInfo({});
      console.error('Failed to fetch reports:', err);
    },
  });

  /**
   * Mark tabs with permissions - but don't remove them, just mark them as disabled
   */
  const markTabsWithPermissions = (tabs: string[]): PowerBiTab[] => {
    return tabs.map((tab) => ({
      value: tab,
      badge: { content: '', color: '', backgroundColor: '' },
      disabled: tabPermissionMap.hasOwnProperty(tab.toLowerCase()) &&
        !tabPermissionMap[tab.toLowerCase()]
    }));
  };

  /**
   * Check if stock API is enabled based on current context
   */
  const isStockApiEnabled = (): boolean => {
    // If no business unit selected, check vendors list (for admin users)
    if (!selectedBusinessUnit) {
      return vendorsList.some(vendor => vendor.isStockApiEnabled);
    }
    
    // If business unit selected but no brands data yet, return false (will be checked in useEffect)
    if (!brandsReq.data || brandsReq.data.length === 0) {
      return false;
    }
    
    // Find the specific brand for the selected business unit
    const selectedBrand = brandsReq.data.find(brand => brand.id === selectedBusinessUnit.brandId);
    return selectedBrand?.brandVendor?.isStockApiEnabled || false;
  };

  /**
   * Determine if user should see stock tab immediately or wait for API call
   */
  const shouldShowStockTabImmediately = (): boolean => {
    const roles: SHRole[] = getItems('roles');
    const userRole = selectedBusinessUnit?.roleId 
      ? roles.find(role => role.id === selectedBusinessUnit?.roleId)?.name
      : user?.role;
    
    const isAdmin = userRole === Role.Admin || userRole === Role.SuperAdmin;
    
    // Admin users with no business unit can show immediately based on vendors list
    return isAdmin && !selectedBusinessUnit;
  };

  /**
   * Process and set report tabs
   */
  const handleReportTabs = (): void => {
    let userRoleId = getRoleId(user?.role);
    const userReports = reports.filter(report => report.userRole === userRoleId);
    // Reports are fetched for getUserRoleIdForReport's role, which is VendorAdmin once an
    // admin has a vendor selected, so a tab with no row for that role could never load.
    const reportRoleId = getUserRoleIdForReport();
    const tabNames = Array.from(new Set(userReports.map(item => item.viewCode)))
      .filter(tabName => findReportByTabAndRole(tabName, reportRoleId));

    // Mark tabs with permissions instead of filtering them
    const tabsWithPermissions = markTabsWithPermissions(tabNames);

    // Handle stock tabs separately
    if (featureFlags.stocks?.enabled) {
      if (shouldShowStockTabImmediately()) {
        // Admin with no business unit - show immediately based on vendors list
        const stockEnabled = isStockApiEnabled();
        setPowerBiTabs(stockEnabled ? tabsWithPermissions : tabsWithPermissions.filter(item => item.value !== STOCKS_REPORT));
      } else {
        // Need to wait for API call - initially hide stock tab
        const stockTab = tabsWithPermissions.filter(item => item.value === STOCKS_REPORT);
        setPowerBiTabs(tabsWithPermissions.filter(item => item.value !== STOCKS_REPORT));
        setStockTab(stockTab);
        
        // Fetch brands data if needed
        const vendorId = selectedBusinessUnit?.vendorId || selectedVendor?.id;
        if (vendorId) {
          brandsReq.run(vendorId);
        }
      }
    } else {
      // If stocks feature is disabled, filter out STOCKS_REPORT
      setPowerBiTabs(tabsWithPermissions.filter(item => item.value !== STOCKS_REPORT));
    }
  };

  /**
   * Get appropriate role ID for report fetching
   */
  const getUserRoleIdForReport = (): number => {
    let roleId = selectedVendor &&
      (user?.role === Role.Admin || user?.role === Role.SuperAdmin)
      ? getRoleId(Role.VendorAdmin)
      : getRoleId(user?.role);

    return roleId;
  };

  /**
   * Find report by tab name and role
   */
  const findReportByTabAndRole = (tabName: string, roleId: number): ReportData | undefined => {
    return reports.find(report =>
      report.viewCode === tabName && report.userRole === roleId
    );
  };

  /**
   * Fetch report data
   */
  const fetchReportData = (report: ReportData, tabName: string): void => {
    getPowerBiReportReq.run(
      [token],
      selectedVendor?.name,
      report.reportId,
      tabName,
      selectedVendor?.id,
      selectedBusinessUnit?.brandId
    );
  };

  // Handle brand data changes - add stock tab if enabled
  useEffect(() => {
    if (brandsReq.data && brandsReq.data.length > 0 && stockTab.length > 0) {
      const stockEnabled = isStockApiEnabled();
      
      if (stockEnabled) {
        setPowerBiTabs(prevTabs => {
          // Avoid duplication - check if stock tab already exists
          const hasStockTab = prevTabs.some(tab => tab.value === STOCKS_REPORT);
          if (hasStockTab) return prevTabs;
          
          return [...prevTabs, ...stockTab];
        });
      }
    }
  }, [brandsReq.data, stockTab, selectedBusinessUnit]);

  // Keep the active tab index valid whenever the tab set is rebuilt.
  // The tab list differs between "All" and a specific business unit (the stocks
  // report is only present in some of them), so a kept index can point at the
  // wrong report or fall out of range entirely.
  useEffect(() => {
    if (powerBiTabs.length === 0) {
      prevTabCountRef.current = 0;
      return;
    }

    const countChanged = prevTabCountRef.current !== powerBiTabs.length;
    prevTabCountRef.current = powerBiTabs.length;

    const currentTab = activeTab !== undefined ? powerBiTabs[activeTab] : undefined;

    // `!currentTab` covers an index that is now out of range
    if (countChanged || !currentTab || currentTab.disabled) {
      // Find the first non-disabled tab
      const firstEnabledTabIndex = powerBiTabs.findIndex(tab => !tab.disabled);
      setActiveTab(firstEnabledTabIndex === -1 ? undefined : firstEnabledTabIndex);
    }
  }, [powerBiTabs, activeTab]);

  // Load reports on mount
  useEffect(() => {
    getPowerBiReports.run([token]);
  }, [token]);

  // Process tabs when data changes
  useEffect(() => {
    if (featureFlags && (vendorsList || isVendor(user!) || isBrandLevelRoles(user!))) {
      handleReportTabs();
    }
  }, [featureFlags, vendorsList, reports, user, selectedBusinessUnit, selectedVendor]);

  // The fetch depends on the open tab, not on the tab array: the array is rebuilt on every
  // vendor or business-unit change, and re-running on that would send a second request.
  const openTab = activeTab !== undefined ? powerBiTabs[activeTab] : undefined;

  // Fetch report data when tab changes
  useEffect(() => {
    setReportInfo({});
    getPowerBiReportReq.cancel();

    if (activeTab !== undefined && user && reports.length > 0 && powerBiTabs.length > 0) {
      const tabName = getTabNameByIndex(activeTab);

      // Only fetch report if the tab is not disabled
      const currentTab = powerBiTabs[activeTab];
      if (currentTab && !currentTab.disabled) {
        const userRoleId = getUserRoleIdForReport();
        const report = findReportByTabAndRole(tabName, userRoleId);

        if (report) {
          fetchReportData(report, tabName);
        }
      }
    }
  }, [selectedVendor, activeTab, user, reports, openTab?.value, openTab?.disabled, selectedBusinessUnit]);

  return {
    powerBiTabs,
    activeTab,
    setActiveTab,
    reportInfo,
    reports,
    setReport,
  };
};
